/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import WaDialog from '@awesome.me/webawesome/dist/components/dialog/dialog.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';

const appStoreUrl = 'https://apps.apple.com/app/apple-store/id6502530457?pt=125677785&ct=row_counter_export&mt=8';
const googlePlayUrl = 'https://play.google.com/store/apps/details?id=studio.okayest.yarnly&referrer=row_counter_export';

// Explains how to import an exported row counter file into the Yarnly app.
@customElement('yl-export-dialog')
export class ExportDialog extends LitElement {
  static styles = css`
    wa-dialog {
      --width: 30rem;
    }

    p,
    ol {
      margin: 0 0 12px;
      line-height: 1.5;
    }

    ol {
      padding-left: 24px;
    }

    li + li {
      margin-top: 8px;
    }

    .file-name {
      font-weight: bold;
      overflow-wrap: anywhere;
    }

    .badges {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      margin: 8px 0 4px;
    }

    .badges img {
      display: block;
      height: 44px;
      width: auto;
    }

    .badges a.app-store img {
      border-radius: 8px;
    }
  `;

  @query('wa-dialog')
  private _dialog!: WaDialog;

  @state()
  private _fileName: string = '';

  show(fileName: string) {
    this._fileName = fileName;
    this._dialog.open = true;
  }

  render() {
    return html`<wa-dialog label="Import into Yarnly" light-dismiss>
      <p>Your row counters were downloaded as <span class="file-name">${this._fileName}</span>.</p>
      <ol>
        <li>
          Get the Yarnly app on your phone or tablet.
          <div class="badges">
            <a class="app-store" href=${appStoreUrl} target="_blank" rel="noopener">
              <img src="/img/download-app-store.svg" alt="Download on the App Store" />
            </a>
            <a href=${googlePlayUrl} target="_blank" rel="noopener">
              <img src="/img/google-play-badge.png" alt="Get it on Google Play" />
            </a>
          </div>
        </li>
        <li>
          If you downloaded the file on a different device, send it to your phone or tablet, for example with AirDrop,
          email, or a cloud drive.
        </li>
        <li>In Yarnly, open <b>Settings</b> from the home screen, then <b>Backup & sync</b>.</li>
        <li>Under <b>Manual Backup</b>, tap <b>Import projects</b> and choose the downloaded file.</li>
      </ol>
      <p>Your counters will appear in a new project called <b>Row Counter Project</b>.</p>
      <wa-button slot="footer" variant="brand" data-dialog="close">Got it</wa-button>
    </wa-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yl-export-dialog': ExportDialog;
  }
}
